/**
 * Game tuning VALUES for the Cannon feature — the win-multiplier-with-queue presentation.
 * Placeholder timings; retune once the Spine cannon art lands. All animation runs on the
 * PresentationTaskRunner (engine time), so these are engine-seconds.
 *
 * The chamber + queue bullets are SPAWNED programmatically from the bullet prefab and SOCKETED to
 * the cannon Spine's placeholder bones (`spine.placeholders`), so they ride the idle/load/shot motion.
 * The fired round rides the `shot` clip itself; the number burst fires on the clip's own event.
 */
export interface CannonTuning {
  /**
   * Runner scope of the blast wait's fallback sleep (`CannonView.waitForBlast`); the fire itself is the Spine `shot`
   * clip, which no slam shortens. Nothing slams this scope: a slammed wait would burst the win before the blast.
   */
  readonly fireScope: string;
  /**
   * The shot + reload sound per shot of a spin, played as the shot clip starts: the Nth shot plays
   * entry N, every shot past the last entry repeats the last. A new spin starts again from the first.
   */
  readonly fireSoundIds: readonly string[];
  /** Uniform scale for each socketed queue/chamber bullet — applied to the bullet CHILD (the socket drives a follower). */
  readonly queueBulletScale: number;
  /** Multiplier label affixes — `${prefix}${value}${suffix}`, e.g. "x" → "x3". */
  readonly labelPrefix: string;
  readonly labelSuffix: string;
  /**
   * Round-load z-order flip. The loading round rides the chamber bone (`nut`) but renders IN FRONT of the
   * `cannon_top` overlay for the first `roundLoadBehindFraction` of the `load` clip (parented to the scene's
   * `roundLoadPlaceholder`, which sits after cannon_top), then reparents BEHIND (into the cannon body node).
   * Runner scope `roundLoadScope`. The flip runs across the load clip's own length, read from the clip as it
   * starts, so retiming the clip in Spine moves the flip with it.
   */
  readonly roundLoadBehindFraction: number;
  readonly roundLoadScope: string;
  /** Cannon-body Spine clips + the name of the `shot` clip's event that marks the blast (the round leaving the muzzle). */
  readonly spine: {
    readonly idleClip: string;
    readonly loadClip: string;
    readonly shotClip: string;
    /**
     * The event the `shot` clip fires at the muzzle blast — the moment the win amount bursts to its
     * multiplied total. The view listens for it, so retiming the clip in Spine moves the burst with it;
     * its authored time in the clip is only the safety net when the event never arrives.
     */
    readonly fireEventName: string;
    /**
     * Slot-name prefix of EVERY spine-drawn round guide. ALL matching slots are hidden at configure
     * (enumerated from the skeleton, so guides beyond the socketed `placeholders` below — e.g. the
     * animator's `cannonball_placeholder7/8` — never render; rounds are exclusively the prefabs).
     */
    readonly guideSlotPrefix: string;
    /**
     * Crossfade between the body clips (every idle/load/shot pair, both layers) via Spine
     * `setMix` — queued transitions (shot → load → idle) and interrupts (idle → shot on a fire)
     * BLEND over this duration instead of snapping poses. 0 = hard cuts.
     */
    readonly mixSeconds: number;
    /**
     * Chamber-first placeholder bones the queue bullets socket onto + the guide slots to hide. One round per
     * bone: the cannon shows the chamber and `length − 1` queued.
     */
    readonly placeholders: readonly { readonly bone: string; readonly slot: string }[];
  };
}

/**
 * Base-game starting multiplier ladder — `[0]` is the chambered bullet (x2), `[1..]` the queue.
 * GAME-LOCAL by design (the cannon is game-specific — it never enters the shared platform's
 * InitialSettings). The LIVE queue is server-authoritative per spin: the backend authors an
 * `apply_win_multiplier` at every reveal (base resets, super_bonus grows), so this constant only
 * SEEDS the pre-response gap — the client reloads it on each BASE spin REQUEST (as the reels start
 * scrolling, the game's `preSpin`) + boot. Between spins the queue HOLDS its prior state (a win's advanced
 * chamber, or the super_bonus grown queue after a bonus) until the next base request reloads it. Keep
 * it matching the backend's base reveal (`chamberedMultiplier "2"` + `["3".."8"]`) so there's no
 * one-frame flash. Free spins never run `preSpin`, so their grown queue persists (server-driven).
 */
export const CANNON_LADDER: readonly string[] = ["2", "3", "4", "5", "6", "7", "8"];

export const CANNON_TUNING: CannonTuning = {
  fireScope: "cannon_fire",
  // Nine takes of the shot, each with a chime a semitone above the last.
  fireSoundIds: Array.from({ length: 9 }, (_, i) => `cannon_shoot_load_pitch_${i + 1}`),
  queueBulletScale: 3,
  labelPrefix: "x",
  labelSuffix: "",
  // No projectile — the number burst fires ON the shot's `explotion` event (the muzzle blast).
  roundLoadBehindFraction: 0.5,
  roundLoadScope: "cannon_round_load",
  spine: {
    idleClip: "idle",
    loadClip: "load",
    shotClip: "shot",
    fireEventName: "explotion",
    // Hide EVERY spine-drawn round guide (incl. the animator's placeholder7/8, which the 6
    // socketed `placeholders` below don't cover) — visible rounds are exclusively the prefabs.
    guideSlotPrefix: "cannonball_placeholder",
    // Crossfade idle/load/shot transitions (queued + interrupts) instead of pose-snapping.
    mixSeconds: 0.12,
    // Chamber first (`nut`, at the muzzle), then the 5 queue positions (`bone3..bone7`, the queue line) —
    // one per placeholder bone in the `animations/spine/cannon_bottom` Spine. `cannonball_placeholder*` are the
    // guide slots (each shows the `nut` region) hidden per position so the socketed bullet renders instead.
    placeholders: [
      { bone: "nut", slot: "cannonball_placeholder" },
      { bone: "bone3", slot: "cannonball_placeholder2" },
      { bone: "bone4", slot: "cannonball_placeholder3" },
      { bone: "bone5", slot: "cannonball_placeholder4" },
      { bone: "bone6", slot: "cannonball_placeholder5" },
      { bone: "bone7", slot: "cannonball_placeholder6" },
    ],
  },
};
